import React, { useState, useMemo } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fab,
  InputAdornment,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Payment as PaymentIcon,
  Link as LinkIcon,
  Undo as ReturnIcon,
  MoreVert as MoreVertIcon,
  ViewModule as CardViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import PaymentRecordDialog from '../../../components/documents/PaymentRecordDialog'
import SalesReturnDialog from '../../../components/documents/SalesReturnDialog'
import InvoiceCard from '../../../components/documents/InvoiceCard'
import InvoiceFilters from '../../../components/documents/InvoiceFilters'
import { documentInvoiceService } from '../../../services/documentInvoiceService'
import { salesReturnService } from '../../../services/salesReturnService'
import { Invoice, InvoiceStatus } from '../../../types/payment'
import { 
  PaymentData, 
  DialogState,
  InvoiceListResponse 
} from '../../../types/invoiceTypes'

// Indian currency formatter
const formatIndianCurrency = (amount: number) => 
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount)

const InvoicesList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [dialogState, setDialogState] = useState<DialogState>({
    type: null,
    invoice: null
  })
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list')
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({})
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  // Fetch invoices from API
  const { data, isLoading, error, refetch } = useQuery<InvoiceListResponse>({
    queryKey: ['invoices', page, pageSize, searchTerm, statusFilter, startDate, endDate],
    queryFn: () => {
      return documentInvoiceService.getInvoices({
        page,
        per_page: pageSize,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        from_date: startDate || undefined,
        to_date: endDate || undefined
      })
    }
  })

  // Calculate payment progress
  const calculatePaymentProgress = (invoice: Invoice) => {
    if (!invoice.total_amount || invoice.total_amount === 0) return 0
    const paid = Number(invoice.paid_amount) || 0
    const total = Number(invoice.total_amount) || 0
    return Math.round((paid / total) * 100)
  }

  // Get outstanding amount
  const getOutstandingAmount = (invoice: Invoice) => {
    return Number(invoice.outstanding_amount) || Number(invoice.balance_amount) || 0
  }

  // Memoize processed invoices
  const processedInvoices = useMemo(() => {
    if (!data?.invoices) return []
    
    return data.invoices.map(invoice => ({
      ...invoice,
      paymentProgress: calculatePaymentProgress(invoice),
      outstandingAmount: getOutstandingAmount(invoice)
    }))
  }, [data?.invoices])

  // Get status configuration
  const getStatusConfig = (status: string) => {
    const statusMap: Record<string, { color: any, label: string }> = {
      draft: { color: 'default', label: 'Draft' },
      sent: { color: 'info', label: 'Sent' },
      partial: { color: 'warning', label: 'Partially Paid' },
      paid: { color: 'success', label: 'Paid' },
      overdue: { color: 'error', label: 'Overdue' },
      cancelled: { color: 'error', label: 'Cancelled' },
    }
    return statusMap[status] || { color: 'default', label: status }
  }

  // Dialog management
  const openDialog = (type: 'payment' | 'salesReturn', invoice: Invoice) => {
    setDialogState({ type, invoice })
  }

  const closeDialog = () => {
    setDialogState({ type: null, invoice: null })
  }

  // Event handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setDialogState({ type: null, invoice })
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setDialogState({ type: null, invoice: null })
  }

  const handleRecordPayment = () => {
    if (dialogState.invoice) {
      openDialog('payment', dialogState.invoice)
    }
    handleMenuClose()
  }

  const handleCreateReturn = () => {
    if (dialogState.invoice) {
      openDialog('salesReturn', dialogState.invoice)
    }
    handleMenuClose()
  }

  const handlePaymentClick = async (invoice: Invoice) => {
    setActionLoading(prev => ({ ...prev, [invoice.id]: true }))
    try {
      openDialog('payment', invoice)
    } finally {
      setActionLoading(prev => ({ ...prev, [invoice.id]: false }))
    }
  }

  const handlePaymentSuccess = async (paymentData: PaymentData) => {
    if (!dialogState.invoice) return

    try {
      // Call the payment API
      const response = await fetch(`/api/admin/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          invoice_id: dialogState.invoice.id,
          payment_date: paymentData.payment_date 
            ? (typeof paymentData.payment_date === 'string' 
                ? paymentData.payment_date 
                : paymentData.payment_date.toISOString().split('T')[0])
            : new Date().toISOString().split('T')[0],
          payment_method: paymentData.payment_method.toLowerCase(),
          amount: paymentData.amount,
          reference_number: paymentData.transaction_id,
          notes: paymentData.notes,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to record payment')
      }

      enqueueSnackbar('Payment recorded successfully!', { variant: 'success' })
      closeDialog()
      
      // Refetch to get updated data
      await refetch()
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to record payment', { variant: 'error' })
      throw error
    }
  }

  const handleReturnSuccess = async (returnData: any) => {
    try {
      await salesReturnService.createSalesReturn(returnData)
      enqueueSnackbar('Sales return created successfully!', { variant: 'success' })
      closeDialog()
      await refetch()
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to create sales return', { variant: 'error' })
      throw error
    }
  }

  const handleCardClick = (invoiceId: string) => {
    navigate(`/admin/documents/invoices/${invoiceId}`)
  }

  const handleClearFilters = () => {
    setStatusFilter('')
    setStartDate('')
    setEndDate('')
    setFilterMenuAnchor(null)
  }

  const handleApplyFilters = () => {
    setFilterMenuAnchor(null)
  }

  const hasActiveFilters = statusFilter || startDate || endDate

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load invoices. Please try again later.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Invoices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.total || 0} total invoices
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/documents/invoices/create')}
          sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
        >
          Create Invoice
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by invoice number, customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="card">
                  <Tooltip title="Card View">
                    <CardViewIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list">
                  <Tooltip title="Table View">
                    <ListViewIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant={hasActiveFilters ? "contained" : "outlined"}
                startIcon={<FilterIcon />}
                onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Filters {hasActiveFilters && `(${[statusFilter, startDate, endDate].filter(Boolean).length})`}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Filter Menu */}
        <InvoiceFilters
          statusFilter={statusFilter}
          startDate={startDate}
          endDate={endDate}
          onStatusChange={setStatusFilter}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onClear={handleClearFilters}
          onApply={handleApplyFilters}
          anchorEl={filterMenuAnchor}
          onClose={() => setFilterMenuAnchor(null)}
        />
      </Paper>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography>Loading invoices...</Typography>
        </Box>
      ) : viewMode === 'list' ? (
        /* Table View */
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Invoice #</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Due Date</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Total</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Paid</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Outstanding</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Payment</TableCell>
                <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedInvoices.map((invoice) => {
                const statusConfig = getStatusConfig(invoice.status)
                const outstandingAmount = getOutstandingAmount(invoice)
                const canRecordPayment = outstandingAmount > 0 && invoice.status !== 'cancelled'
                
                return (
                  <TableRow 
                    key={invoice.id}
                    hover
                    onClick={() => handleCardClick(invoice.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        {invoice.invoice_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {invoice.customer_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {format(new Date(invoice.invoice_date), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell>
                      {format(new Date(invoice.due_date), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusConfig.label}
                        color={statusConfig.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatIndianCurrency(Number(invoice.total_amount) || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="success.main">
                        {formatIndianCurrency(Number(invoice.paid_amount) || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={outstandingAmount > 0 ? 'error.main' : 'text.secondary'}>
                        {formatIndianCurrency(outstandingAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ width: 60 }}>
                        <Typography variant="caption" display="block" gutterBottom>
                          {invoice.paymentProgress}%
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={invoice.paymentProgress} 
                          sx={{ height: 6, borderRadius: 1 }}
                          color={invoice.paymentProgress === 100 ? 'success' : 'primary'}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {canRecordPayment && (
                          <Tooltip title="Record Payment">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handlePaymentClick(invoice)}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="More Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, invoice)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Card View */
        <Grid container spacing={2}>
          {processedInvoices.map((invoice) => (
            <Grid item xs={12} sm={6} md={4} key={invoice.id}>
              <InvoiceCard
                invoice={invoice}
                onCardClick={handleCardClick}
                onMenuClick={handleMenuClick}
                onPaymentClick={handlePaymentClick}
                isActionLoading={actionLoading[invoice.id]}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {data && data.total > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              sx={{ borderRadius: 2 }}
            >
              Previous
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              <Typography variant="body2">
                Page {page} of {Math.ceil(data.total / pageSize)}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              disabled={page >= Math.ceil(data.total / pageSize)}
              onClick={() => setPage(page + 1)}
              sx={{ borderRadius: 2 }}
            >
              Next
            </Button>
          </Stack>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 200 }
          }
        }}
      >
        <MenuItem onClick={() => {
          if (dialogState.invoice) {
            navigate(`/admin/documents/invoices/${dialogState.invoice.id}`)
          }
          handleMenuClose()
        }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (dialogState.invoice) {
            navigate(`/admin/documents/invoices/${dialogState.invoice.id}/edit`)
          }
          handleMenuClose()
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        {dialogState.invoice && getOutstandingAmount(dialogState.invoice) > 0 && dialogState.invoice.status !== 'cancelled' && (
          <MenuItem onClick={handleRecordPayment}>
            <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Record Payment</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Generate Payment Link</ListItemText>
        </MenuItem>
        {dialogState.invoice && (dialogState.invoice.status === 'paid' || dialogState.invoice.status === 'partial') && (
          <MenuItem onClick={handleCreateReturn}>
            <ListItemIcon><ReturnIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Create Sales Return</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Payment Record Dialog */}
      {dialogState.invoice && (
        <PaymentRecordDialog
          open={dialogState.type === 'payment'}
          invoice={dialogState.invoice}
          onClose={closeDialog}
          onSubmit={handlePaymentSuccess}
        />
      )}

      {/* Sales Return Dialog */}
      {dialogState.invoice && (
        <SalesReturnDialog
          open={dialogState.type === 'salesReturn'}
          invoice={{
            id: dialogState.invoice.id,
            invoice_number: dialogState.invoice.invoice_number,
            status: dialogState.invoice.status,
            items: []
          }}
          onClose={closeDialog}
          onSubmit={handleReturnSuccess}
        />
      )}

      {/* Floating Action Button */}
      <Fab
        color="secondary"
        onClick={() => navigate('/admin/documents/invoices/create')}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  )
}

export default InvoicesList
